import {
  faArrowDown,
  faArrowUp,
  faEllipsisV,
  faPlus,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'
import { ActionIcon, Button, Flex, Menu, Tooltip } from '@mantine/core'
import { useHover } from '@mantine/hooks'

import {
  useMutationDeleteItemCategory,
  useMutationMoveItemCategory,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreItemCategory } from '@flecto/types'
import { Icon, useNotifications } from '@flecto/ui'

import {
  useModalCategoryUpdate,
  useModalCategoryDelete,
} from '../../../categories'
import { useCategoryMoving } from './useCategoryMoving'

const MOVE_UP_OFFSET = -1
const MOVE_DOWN_OFFSET = 1

export type CellActionsProps = {
  item?: CoreItemCategory
}

export function CellActions({ item }: CellActionsProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_inventory' })
  const { showNotification } = useNotifications()
  const { openModal: openCategoryDeleteModal } = useModalCategoryDelete()
  const { mutateAsync: moveCategory } = useMutationMoveItemCategory()
  const { hovered, ref } = useHover()
  const { openModal: openNewSubCategoryModal } = useModalCategoryUpdate()
  const { shouldDisableMoveUpButton, shouldDisableMoveDownButton } =
    useCategoryMoving(item)
  const { mutateAsync: deleteCategory } = useMutationDeleteItemCategory()

  const handleCategoryDelete = async () => {
    if (
      (item?.items_count || 0) <= 0 &&
      (item?.subcategories.length || 0) <= 0 &&
      item?.internal_id
    ) {
      try {
        await deleteCategory({ categoryId: item?.internal_id })
        showNotification({
          type: 'success',
          message: t('notification_delete_category_success'),
        })
      } catch (_) {
        showNotification({
          type: 'error',
          message: t('notification_delete_category_error'),
        })
      }
    } else {
      openCategoryDeleteModal({ category: item })
    }
  }

  const handleMoveCategory = async (
    event: React.MouseEvent<HTMLElement>,
    offset: number
  ) => {
    event.stopPropagation()

    try {
      await moveCategory({
        offset,
        internal_id: Number(item?.internal_id),
      })

      showNotification({
        type: 'success',
        message:
          offset === MOVE_UP_OFFSET
            ? t('notification_move_up_category_success')
            : t('notification_move_down_category_success'),
      })
    } catch (_) {
      showNotification({
        type: 'error',
        message: t('notification_move_category_error'),
      })
    }
  }

  return (
    <Flex align='center' gap='sm' justify='flex-end' ref={ref}>
      {hovered && (
        <Flex>
          <Button.Group>
            <Tooltip label={t('table.action_move_up')} withinPortal>
              <Button
                px='sm'
                size='sm'
                variant='default'
                disabled={shouldDisableMoveUpButton}
                onClick={(event) => handleMoveCategory(event, MOVE_UP_OFFSET)}
              >
                <Icon icon={faArrowUp} />
              </Button>
            </Tooltip>
            <Tooltip label={t('table.action_move_down')} withinPortal>
              <Button
                px='sm'
                size='sm'
                variant='default'
                disabled={shouldDisableMoveDownButton}
                onClick={(event) => handleMoveCategory(event, MOVE_DOWN_OFFSET)}
              >
                <Icon icon={faArrowDown} />
              </Button>
            </Tooltip>
          </Button.Group>
        </Flex>
      )}
      <Menu withinPortal position='bottom-end'>
        <Menu.Target>
          <ActionIcon
            size='sm'
            variant='default'
            onClick={(e) => e.stopPropagation()}
          >
            <Icon icon={faEllipsisV} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
          {!item?.parent_id ? (
            <>
              <Menu.Item
                leftSection={<Icon icon={faPlus} color='gray.7' />}
                onClick={() =>
                  openNewSubCategoryModal({
                    category: { parent_id: item?.id },
                  })
                }
              >
                {t('table.action_new_category')}
              </Menu.Item>
              <Menu.Item
                leftSection={<Icon icon={faTrash} color='gray.7' />}
                onClick={() => handleCategoryDelete()}
              >
                {t('table.action_delete_category')}
              </Menu.Item>
            </>
          ) : (
            <Menu.Item
              leftSection={<Icon icon={faTrash} color='gray.7' />}
              onClick={() => handleCategoryDelete()}
            >
              {t('table.action_delete_category')}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </Flex>
  )
}
